/**
 * 광고 성과 규칙기반 인사이트 — LLM 없이 정적 로직으로 코멘트 생성.
 * AdReport 페이지가 즉시 표시하는 기본 인사이트. AI 인사이트는 별도(온디맨드).
 * daily.roas·summary는 자사 실결제 순매출 기준. 채널별 conversionValue는 인스타(META)만
 * 유효한 참고값이다 — 네이버(스마트스토어)는 전환매출을 측정할 수 없다.
 */

interface DailyChannel {
  spend: number
  revenue: number
}

/** buildRuleInsights 가 필요로 하는 최소 형태 (AdReport 응답의 부분집합). */
export interface AdsReportForInsights {
  summary: {
    spend: { current: number }
    revenue: { current: number }
  }
  daily: {
    date: string
    spend: number
    revenue: number
    roas: number | null
    byChannel: { META: DailyChannel; NAVER: DailyChannel }
  }[]
  channels: { channel: string; spend: number; conversionValue: number; roas: number | null }[]
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`
}

/**
 * 규칙기반 인사이트 최대 3개 생성 (모두 자사 실결제 순매출/ROAS 기준).
 * 1) 전체 ROAS 수준(손익분기 대비), 2) 일자별 급락, 3) 채널 지출 비중.
 * 채널별 전환매출은 인스타만 측정 가능해 채널 간 ROAS 비교는 하지 않는다.
 */
export function buildRuleInsights(report: AdsReportForInsights): string[] {
  const insights: string[] = []

  const meta = report.channels.find((c) => c.channel === 'META')
  const naver = report.channels.find((c) => c.channel === 'NAVER')
  const totalSpend = report.summary.spend.current
  const totalRev = report.summary.revenue.current

  // 1) 전체 ROAS 수준 — 실결제 순매출/광고비. 100% 미만이면 광고비가 매출을 초과.
  if (totalSpend > 0) {
    const roas = totalRev / totalSpend
    if (roas < 1) {
      insights.push(
        `전체 ROAS(실결제) ${pct(roas)} — 광고비가 순매출을 초과합니다. 소재·타겟 효율을 점검하거나 지출을 조이는 걸 검토하세요.`,
      )
    } else {
      insights.push(
        `전체 ROAS(실결제) ${pct(roas)} — 광고비 대비 순매출이 흑자입니다. 효율 유지 여부를 보며 증액 여지를 살펴보세요.`,
      )
    }
  }

  // 2) 일자별 급락 (ROAS 있는 날만, 인접일 비교)
  const withRoas = report.daily.filter((d) => d.roas != null) as (typeof report.daily[number] & { roas: number })[]
  if (withRoas.length >= 2) {
    let maxDropIdx = -1
    let maxDrop = 0
    for (let i = 1; i < withRoas.length; i++) {
      const prev = withRoas[i - 1].roas
      const cur = withRoas[i].roas
      if (prev > 0) {
        const drop = (prev - cur) / prev
        if (drop > maxDrop) {
          maxDrop = drop
          maxDropIdx = i
        }
      }
    }
    if (maxDropIdx > 0 && maxDrop >= 0.3) {
      const prev = withRoas[maxDropIdx - 1]
      const cur = withRoas[maxDropIdx]
      insights.push(
        `${cur.date} ROAS(실결제) 급락 — ${pct(prev.roas)}→${pct(cur.roas)}. 광고비 대비 순매출이 크게 줄었습니다. 소재/타겟 피로도를 먼저 점검하세요.`,
      )
    }
  }

  // 3) 채널 지출 비중 — 한쪽에 지출이 쏠려 있으면 분산 여지 안내.
  if (meta && naver && totalSpend > 0) {
    const metaShare = meta.spend / totalSpend
    if (metaShare >= 0.85) {
      insights.push(
        `광고비의 ${pct(metaShare)}가 인스타에 집중돼 있습니다. 네이버 검색광고 비중을 늘려 채널을 분산하는 것도 검토해볼 만합니다.`,
      )
    } else if (metaShare <= 0.15) {
      insights.push(
        `광고비의 ${pct(1 - metaShare)}가 네이버에 집중돼 있습니다. 인스타 비중을 늘려 채널을 분산하는 것도 검토해볼 만합니다.`,
      )
    }
  }

  return insights.slice(0, 3)
}
